import * as React from "react";
import {
    ICustomerPortalService,
    Policy,
    resourcesHelper,
    Types,
    PRODUCT_ANNUAL_HOME,
    PRODUCT_WOLF_MOTOR
} from "@falcon/services";
import {authorize} from "@athena-portal/components";
import {PluginTemplates} from "../../../utils/PluginTemplates";
import FalconHeader from "../../../components/navigation/FalconHeader";
import FalconFooter from "../../../components/navigation/FalconFooter";
import {lazyInject} from "@athena-portal/core";
import {ButtonLink} from "../../../components/navigation/ButtonLink";
import {finalize} from "rxjs/operators";

interface IState {
    oldCarPolicies: Policy[];
    oldHomePolicies: Policy[];
    pageLoaded: boolean;
}

@authorize
export class OldPoliciesDashboardPage extends React.Component<{}, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    private pluginTemplates = new PluginTemplates();

    constructor(props: {}) {
        super(props);
        this.state = {
            oldCarPolicies: [],
            oldHomePolicies: [],
            pageLoaded: false,
        };
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Old policies");

        this.setState({pageLoaded: false}, () =>
            this.customerPortalService.getExpiredPolicies()
                .pipe(finalize(() => this.setState({pageLoaded: true})))
                .subscribe(policies => {
                    this.setState({
                        oldCarPolicies: this.filterPolicies(policies, PRODUCT_WOLF_MOTOR),
                        oldHomePolicies: this.filterPolicies(policies, PRODUCT_ANNUAL_HOME)
                    });
                })
        );

        this.setState({pageLoaded: true}, () =>
            this.customerPortalService.getPolicyHistoryAfterRenewal()
                .pipe(finalize(() => this.setState({pageLoaded: true})))
                .subscribe((policyHistories: Policy[]) => {
                    const carPoliciesBeforeRenewal = this.filterPolicies(policyHistories, PRODUCT_WOLF_MOTOR);
                    const homePoliciesBeforeRenewal = this.filterPolicies(policyHistories, PRODUCT_ANNUAL_HOME);

                    const oldCarPolicies = this.state.oldCarPolicies.concat(carPoliciesBeforeRenewal);
                    const oldHomePolicies = this.state.oldHomePolicies.concat(homePoliciesBeforeRenewal);

                    this.setState({
                        oldCarPolicies: oldCarPolicies || [],
                        oldHomePolicies: oldHomePolicies || [],
                    });
                })
        );
    }

    public render(): React.ReactNode {
        return (
            <React.Fragment>
                <FalconHeader/>
                <div className="page-contents dashboard-page">
                    {this.renderMenuTiles()}
                </div>
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private renderMenuTiles() {
        const HOME_OLD_POLICIES = "/dashboard/annualHomeOldPolicies";
        const CAR_OLD_POLICIES = "/dashboard/annualMotorOldPolicies";
        const hasOldCarPolicies: boolean = this.state.oldCarPolicies.length > 0;
        const hasOldHomePolicies: boolean = this.state.oldHomePolicies.length > 0;
        const isBritannia = resourcesHelper.THEME === "Britannia";

        return <>
            {this.pluginTemplates.renderBackLink("Back", true, `/dashboard/`)}
            <p className="mb-1 mt-5 margin-bottom-32 h1">View Old policies</p>
            <div className="d-flex flex-wrap">

                {this.state.pageLoaded &&
                    <button className={`flexItem panel policy-panel ${!hasOldCarPolicies ? 'disabled' : ''}`}
                         onClick={this.navigateTo(CAR_OLD_POLICIES)}>
                        <img className="car-front-icon" src={`/${resourcesHelper.THEME}/img/wolf-your-car.png`}
                             alt="Vehicle icon"/>
                        <div><b>Annual Car Insurance</b></div>
                        <ButtonLink to={CAR_OLD_POLICIES} disabled={!hasOldCarPolicies} primaryNoArrow={true}>
                            Car Old Policies
                        </ButtonLink>
                        {!hasOldCarPolicies && <h6>You don't have old car policies</h6>}
                        {hasOldCarPolicies && <h6>You have <b>{this.state.oldCarPolicies.length}</b> old
                            car {this.state.oldCarPolicies.length === 1 ? 'policy' : 'policies'}</h6>}
                    </button>}

                {this.state.pageLoaded && !isBritannia &&
                    <button className={`flexItem panel policy-panel ${!hasOldHomePolicies ? 'disabled' : ''}`}
                         onClick={this.navigateTo(HOME_OLD_POLICIES)}>
                        <img className="home-icon" alt="Home icon" src={`/${resourcesHelper.THEME}/img/home-icon.png`}/>
                        <div><b>Annual Home Insurance</b></div>
                        <ButtonLink to={HOME_OLD_POLICIES} disabled={!hasOldHomePolicies} primaryNoArrow={true}>
                            Home old policies
                        </ButtonLink>
                        {!hasOldHomePolicies && <h6>You don't have old home policies</h6>}
                        {hasOldHomePolicies &&
                            <h6>You have <b>{this.state.oldHomePolicies.length}</b> old
                                home {this.state.oldHomePolicies.length === 1 ? 'policy' : 'policies'}</h6>}
                    </button>}

            </div>
            {this.pluginTemplates.renderBackLink("Back", false, `/dashboard/`)}
        </>;
    }

    private filterPolicies(policies: Policy[], productType: string): Policy[] {
        return policies.filter(p => p.products.includes(productType));
    }

    private navigateTo(url: string) {
        return () => window.location.assign(url);
    }
}
